/**
 * Card "Sugestão IA" — mostra um plano de treino gerado pelo modelo
 * a partir do onboarding do aluno, e oferece botão pra criar rotina
 * a partir dele.
 *
 * Só renderiza algo se `suggestion` veio (não-null). Em modo edição
 * do perfil o orquestrador esconde este componente.
 *
 * Props:
 *   - studentId: usado no push da rota new-from-suggestion
 *   - suggestion: sugestão atual do hook useOnboardingSuggestion
 *     (passada por prop em vez de re-chamar o hook, pq a mesma
 *     sugestão é usada também no OnboardingModal — economia de fetch).
 *
 * Decisões:
 *   - Mostra só 3 dias da semana e 3 exercícios por dia (com "…" se
 *     houver mais). UX: card pra dar amostra, detalhes na próxima tela.
 *   - Badge "Nova" só aparece com status === 'pending'.
 */

import { memo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { OnboardingSuggestion } from '@/lib/types';
import { sharedStyles } from './styles';

interface Props {
  studentId: string;
  suggestion: OnboardingSuggestion | null;
}

function AISuggestionSectionImpl({ studentId, suggestion }: Props) {
  const router = useRouter();
  if (!suggestion) return null;

  return (
    <View style={sharedStyles.section}>
      <Text style={sharedStyles.sectionTitle}>Sugestão IA</Text>
      <View style={[sharedStyles.card, styles.suggestionCard]}>
        <View style={styles.suggestionHeader}>
          <Text style={styles.suggestionIcon}>🤖</Text>
          <Text style={styles.suggestionLabel}>Sugestão IA</Text>
          {suggestion.status === 'pending' && (
            <View style={styles.newBadge}>
              <Text style={styles.newBadgeText}>Nova</Text>
            </View>
          )}
        </View>
        <Text style={styles.suggestionSummary}>{suggestion.summary}</Text>
        {suggestion.plan_json.days.slice(0, 3).map((day, i) => (
          <View key={i} style={styles.suggestionDayRow}>
            <Text style={styles.suggestionDayName}>{day.name}</Text>
            <Text style={styles.suggestionDayExercises}>
              {day.exercises.slice(0, 3).map(e => e.name).join(', ')}
              {day.exercises.length > 3 ? '…' : ''}
            </Text>
          </View>
        ))}
        <TouchableOpacity
          style={styles.suggestionBtn}
          onPress={() =>
            router.push(`/(personal)/routines/new-from-suggestion?suggestionId=${suggestion.id}&studentId=${studentId}` as any)
          }
        >
          <Text style={styles.suggestionBtnText}>Criar rotina com esta sugestão →</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  suggestionCard: { borderWidth: 1.5, borderColor: '#C8102E' },
  suggestionHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 },
  suggestionIcon: { fontSize: 16 },
  suggestionLabel: { fontSize: 13, fontWeight: '700', color: '#C8102E', flex: 1 },
  newBadge: { backgroundColor: '#fff3f5', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 2 },
  newBadgeText: { fontSize: 11, color: '#C8102E', fontWeight: '600' },
  suggestionSummary: { fontSize: 12, color: '#555', lineHeight: 18, marginBottom: 10 },
  suggestionDayRow: { backgroundColor: '#f5f5f5', borderRadius: 6, padding: 8, marginBottom: 4 },
  suggestionDayName: { fontSize: 11, fontWeight: '700', color: '#1A1A1A' },
  suggestionDayExercises: { fontSize: 10, color: '#888', marginTop: 1 },
  suggestionBtn: { backgroundColor: '#C8102E', borderRadius: 8, padding: 10, alignItems: 'center', marginTop: 8 },
  suggestionBtnText: { color: '#fff', fontWeight: '700', fontSize: 13 },
});

export const AISuggestionSection = memo(AISuggestionSectionImpl);
